import type { SectionProps } from "../context";
import { Container, Img, Rail, STACK, STAGGER, SectionHeader, SectionShell, Slideshow, cx } from "../ui";

/** Repeating 6-tile mosaic rhythm; grid-flow-dense fills any gaps. */
const MOSAIC = [
  "col-span-2 row-span-2",
  "row-span-1",
  "row-span-2",
  "row-span-1",
  "col-span-2 row-span-1",
  "row-span-1",
] as const;

/** Hand-placed print tilts for the polaroid wall. */
const TILT = ["-rotate-2", "rotate-1", "rotate-2", "-rotate-1", "rotate-[1.5deg]", "-rotate-[1.5deg]"] as const;

const ZOOM = "transition-transform duration-700 ease-out group-hover:scale-[1.04] motion-reduce:transition-none";

/** Hover veil with the alt text as a caption (pointer devices only; the alt is still the label). */
function Veil({ alt }: { alt?: string }) {
  if (!alt) return null;
  return (
    <span
      aria-hidden="true"
      className="pointer-events-none absolute inset-x-0 bottom-0 translate-y-2 bg-linear-to-t from-black/70 to-transparent px-4 pt-10 pb-3 text-left text-sm font-medium text-white opacity-0 transition duration-300 group-hover:translate-y-0 group-hover:opacity-100 motion-reduce:transition-none"
    >
      <span className="line-clamp-2">{alt}</span>
    </span>
  );
}

export function Gallery({ node, ctx }: SectionProps<"gallery">) {
  const c = node.content;
  const hasTitle = !!c.title;
  const images = c.images;
  const headerGap = hasTitle || c.eyebrow ? STACK : undefined;
  const empty = ctx.editor ? <p className={cx(STACK, "rounded-site-md border border-dashed border-site-border p-8 text-center text-sm text-site-muted")}>Tambahkan foto ke galeri.</p> : null;

  if (node.variant === "carousel" && images.length > 0) {
    return (
      <SectionShell ctx={ctx} node={node} labelledBy={hasTitle}>
        <Container size="wide">
          <SectionHeader ctx={ctx} node={node} eyebrow={c.eyebrow} title={c.title} />
          <div className={headerGap}>
            <Rail label={c.title ? `Galeri: ${c.title}` : "Galeri foto"}>
              {images.map((img, i) => (
                <li key={i} className="group relative aspect-[4/5] w-[78%] shrink-0 snap-start overflow-hidden rounded-site-lg bg-site-surface shadow-site-sm @md:w-[46%] @3xl:w-[32%] @5xl:w-[24%]">
                  <Img ctx={ctx} image={img} sizes="(min-width: 1280px) 25vw, (min-width: 768px) 33vw, 80vw" path={`content.images.${i}`} className={ZOOM} />
                  <Veil alt={img.alt} />
                </li>
              ))}
            </Rail>
          </div>
        </Container>
      </SectionShell>
    );
  }

  if (node.variant === "slideshow" && images.length > 0) {
    return (
      <SectionShell ctx={ctx} node={node} labelledBy={hasTitle}>
        <Container size="wide">
          <SectionHeader ctx={ctx} node={node} eyebrow={c.eyebrow} title={c.title} align="center" />
          <div className={headerGap}>
            <Slideshow
              ctx={ctx}
              label={c.title ? `Galeri: ${c.title}` : "Galeri foto"}
              trackClassName="aspect-[4/3] overflow-hidden rounded-site-lg bg-site-surface shadow-site-sm @3xl:aspect-[16/9]"
              controlsClassName="mt-4 justify-center"
              slides={images.map((img, i) => (
                <>
                  <Img ctx={ctx} image={img} sizes="(min-width: 1280px) 1200px, 100vw" path={`content.images.${i}`} target={1600} />
                  {img.alt ? (
                    <p aria-hidden="true" className="pointer-events-none absolute inset-x-0 bottom-0 bg-linear-to-t from-black/70 to-transparent px-5 pt-12 pb-4 text-sm font-medium text-white @3xl:px-8 @3xl:pb-6 @3xl:text-base">
                      <span className="line-clamp-2 max-w-2xl">{img.alt}</span>
                    </p>
                  ) : null}
                </>
              ))}
              thumbs={images.map((img, i) => (
                <Img key={i} ctx={ctx} image={img} sizes="112px" target={240} />
              ))}
            />
          </div>
        </Container>
      </SectionShell>
    );
  }

  if (node.variant === "masonry" && images.length > 0) {
    return (
      <SectionShell ctx={ctx} node={node} labelledBy={hasTitle}>
        <Container size="wide">
          <SectionHeader ctx={ctx} node={node} eyebrow={c.eyebrow} title={c.title} align="center" />
          <ul className={cx(headerGap, STAGGER, "columns-2 gap-3 @3xl:columns-3 @3xl:gap-4 @5xl:columns-4")}>
            {images.map((img, i) => (
              <li key={i} className="group relative mb-3 break-inside-avoid overflow-hidden rounded-site-md bg-site-surface @3xl:mb-4">
                <Img
                  ctx={ctx}
                  image={img}
                  sizes="(min-width: 1280px) 25vw, (min-width: 768px) 33vw, 50vw"
                  path={`content.images.${i}`}
                  className={cx("h-auto!", ZOOM)}
                />
                <Veil alt={img.alt} />
              </li>
            ))}
          </ul>
        </Container>
      </SectionShell>
    );
  }

  if (node.variant === "polaroid" && images.length > 0) {
    return (
      <SectionShell ctx={ctx} node={node} labelledBy={hasTitle}>
        <Container size="wide">
          <SectionHeader ctx={ctx} node={node} eyebrow={c.eyebrow} title={c.title} align="center" />
          <ul className={cx(headerGap, STAGGER, "grid grid-cols-2 gap-x-4 gap-y-8 px-1 @3xl:grid-cols-3 @3xl:gap-x-8 @3xl:gap-y-12 @5xl:grid-cols-4")}>
            {images.map((img, i) => (
              <li key={i}>
                <div
                  className={cx(
                    "bg-white p-2 pb-3 shadow-site-md transition-[rotate,translate,box-shadow] duration-300 hover:-translate-y-1 hover:rotate-0 hover:shadow-site-lg motion-reduce:transition-none @3xl:p-3 @3xl:pb-4",
                    TILT[i % TILT.length],
                  )}
                >
                  <div className="aspect-square overflow-hidden bg-neutral-100">
                    <Img ctx={ctx} image={img} sizes="(min-width: 1280px) 25vw, (min-width: 768px) 33vw, 50vw" path={`content.images.${i}`} />
                  </div>
                  {img.alt ? <p aria-hidden="true" className="baze-heading mt-2.5 line-clamp-1 text-center text-sm text-neutral-700 @3xl:mt-3 @3xl:text-base">{img.alt}</p> : <div className="h-5 @3xl:h-6" />}
                </div>
              </li>
            ))}
          </ul>
        </Container>
      </SectionShell>
    );
  }

  // The mosaic rhythm needs enough tiles; with fewer it falls back to the even grid.
  const mosaic = node.variant === "mosaic" && images.length >= 5;
  return (
    <SectionShell ctx={ctx} node={node} labelledBy={hasTitle}>
      <Container size={mosaic ? "wide" : "default"}>
        <SectionHeader ctx={ctx} node={node} eyebrow={c.eyebrow} title={c.title} />
        {images.length > 0 ? (
          <ul
            className={cx(
              headerGap,
              STAGGER,
              mosaic
                ? "grid grid-flow-dense auto-rows-[9rem] grid-cols-2 gap-3 @md:auto-rows-[11rem] @3xl:auto-rows-[12rem] @3xl:grid-cols-4 @3xl:gap-4 @5xl:auto-rows-[14rem]"
                : "grid grid-cols-2 gap-3 @3xl:grid-cols-3 @3xl:gap-4",
            )}
          >
            {images.map((img, i) => (
              <li
                key={i}
                className={cx(
                  "group relative overflow-hidden rounded-site-md bg-site-surface",
                  mosaic ? MOSAIC[i % MOSAIC.length] : "aspect-square @3xl:aspect-[4/3]",
                  // A lone last tile in a 2-column grid spans the row instead of leaving a hole.
                  !mosaic && images.length % 2 === 1 && i === images.length - 1 && "col-span-2 aspect-[2/1] @3xl:col-span-1 @3xl:aspect-[4/3]",
                )}
              >
                <Img
                  ctx={ctx}
                  image={img}
                  sizes={mosaic && i % MOSAIC.length === 0 ? "(min-width: 768px) 50vw, 100vw" : "(min-width: 768px) 33vw, 50vw"}
                  path={`content.images.${i}`}
                  className={ZOOM}
                />
                <Veil alt={img.alt} />
              </li>
            ))}
          </ul>
        ) : (
          empty
        )}
      </Container>
    </SectionShell>
  );
}
